import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPLETE_DETAIL_FIELD, SELECT_DETAIL_FIELD } from '@/consts/events';
import { RCta } from '../RCta';
import { WregRadioGroup } from '../WregRadioGroup';
import type { WregScreenEvent } from './wregScreenEvents';
import {
  FIELD_NAME,
  INTAKE_ABOUT_FIELDS,
  useIntakeErrors,
  type IntakeErrorKey,
} from './wregIntakeErrors';
import { WregIntakeShell } from './WregIntakeShell';
import { CalendarIcon, MaskedRow } from './WregIntakeRows';
import {
  EMPTY_INTAKE,
  formatDob,
  intakeAboutIsComplete,
  type WregIntakeForm,
} from './wregIntakeFields';

type Props = {
  notice?: string | null;
  initial?: WregIntakeForm;
  onContinue: (patch: Partial<WregIntakeForm>) => void;
  onEvent?: WregScreenEvent;
};

const GENDERS: [string, string][] = [
  ['Female', 'wreg.intake.gender.f'],
  ['Male', 'wreg.intake.gender.m'],
];

const WregIntakeAbout = ({ notice, initial, onContinue, onEvent }: Props) => {
  const { t } = useTranslation();
  const [f, setF] = useState<WregIntakeForm>(initial ?? EMPTY_INTAKE);

  const valid = intakeAboutIsComplete(f);
  const { errors, touch, focus, blur, showError, errorId, validateBeforeSubmit } = useIntakeErrors({
    fields: INTAKE_ABOUT_FIELDS,
    form: f,
    valid,
    step: 'intake-about',
    t,
    onEvent,
  });

  const row = (field: IntakeErrorKey) => ({
    invalid: showError(field),
    errorId: errorId(field),
    error: showError(field) ? errors[field] : undefined,
    onFocus: focus(field),
    onBlur: blur(field),
  });

  const pickGender = (gender: string) => {
    onEvent?.(SELECT_DETAIL_FIELD, { field: FIELD_NAME.gender });
    touch('gender')();
    setF((p) => ({ ...p, gender }));
    onEvent?.(COMPLETE_DETAIL_FIELD, { field: FIELD_NAME.gender, is_valid: true });
  };

  return (
    <WregIntakeShell
      step="intake-about"
      title={t('wreg.intake.about.title')}
      notice={notice}
      onEvent={onEvent}
      footer={
        <>
          <RCta
            beforeClick={validateBeforeSubmit}
            dimmed={!valid}
            onClick={() =>
              onContinue({ dob: f.dob, gender: f.gender })
            }
          >
            {t('wreg.intake.next')}
          </RCta>
        </>
      }
    >
      <MaskedRow
        id="wreg-int-dob"
        label={t('wreg.intake.dob.label')}
        autoComplete="off"
        inputMode="numeric"
        lead={<CalendarIcon />}
        value={f.dob}
        display={f.dob}
        mask={t('wreg.intake.dob.ph')}
        onChange={(e) => setF((p) => ({ ...p, dob: formatDob(e.target.value) }))}
        {...row('dob')}
      />
      <div className="rg-field">
        <span className="rg-label" id="wreg-int-gender-label">
          {t('wreg.intake.gender.label')}
        </span>
        <WregRadioGroup
          id="wreg-int-gender"
          value={f.gender === '' ? null : f.gender}
          onSelect={pickGender}
          options={GENDERS}
          labelledBy="wreg-int-gender-label"
          describedBy={showError('gender') ? errorId('gender') : undefined}
          invalid={showError('gender')}
        />
        {showError('gender') && (
          <span className="rg-error-hint" id={errorId('gender')}>
            {errors.gender}
          </span>
        )}
      </div>
    </WregIntakeShell>
  );
};

export { WregIntakeAbout };
